import React from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo2.png";
import { FaArrowLeft } from "react-icons/fa";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-bold text-gray-900 text-base">{title}</h2>
      {children}
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-gray-600 leading-relaxed">
      {items.map((it) => (
        <li key={it}>{it}</li>
      ))}
    </ul>
  );
}

export default function TermsOfUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 shrink-0">
              <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">UCH Self Service</p>
              <p className="text-gray-400 text-[11px]">University College Hospital</p>
            </div>
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3d2e] border border-green-200 rounded-xl px-4 py-2 hover:bg-green-50 transition mb-6"
        >
          <FaArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 space-y-7">
          <div>
            <p className="text-xs font-bold text-green-700 uppercase tracking-widest mb-2">
              University College Hospital, Ibadan
            </p>
            <h1 className="text-3xl font-black text-gray-900 mb-1.5">
              Terms and Conditions for Hospital Wallet Platform
            </h1>
          </div>

          <Section title="Introduction">
            <p className="text-sm text-gray-600 leading-relaxed">
              These Terms and Conditions (&ldquo;Terms&rdquo;) govern the use of the Hospital
              Wallet Platform (&ldquo;the Wallet&rdquo;), a digital payment and deposit
              system operated by University College Hospital, Ibadan (&ldquo;the
              Hospital&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;).
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              By registering, funding, or using the Wallet, you acknowledge that you have
              read, understood, and agreed to be bound by these Terms.
            </p>
          </Section>

          <Section title="1. Definitions">
            <p className="text-sm text-gray-600 leading-relaxed">For purposes of these Terms:</p>
            <List
              items={[
                "“Wallet” means the virtual payment account assigned to a patient or user for managing hospital-related payments, deposits, or refunds.",
                "“User” or “You” means any individual or authorized representative who registers to use the Hospital Wallet.",
                "“Next of Kin” refers to a person duly recorded in the patient's medical file or registration record as their emergency contact.",
                "“Unspent Balance” means the remaining funds in a Wallet after payment for hospital services has been completed.",
                "“Letter of Administration” means an official document issued by a competent court or authority empowering a person to administer a deceased person's estate.",
                "“Medical Record” means the official documentation of a patient's treatment, diagnosis, and personal details maintained by the Hospital.",
              ]}
            />
          </Section>

          <Section title="2. Purpose and Scope">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital Wallet is provided for the purpose of facilitating seamless
              payment for medical consultations, laboratory tests, admissions, drugs, and
              other hospital-related services; enabling patients or their sponsors to
              pre-fund their healthcare expenses; and providing a secure channel for managing
              deposits and refunds in accordance with hospital policy.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              The Wallet does not function as a conventional bank account, savings account,
              or interest-bearing facility.
            </p>
          </Section>

          <Section title="3. Account Creation and Access">
            <p className="text-sm text-gray-600 leading-relaxed">
              To access the Wallet, users must complete registration through the hospital's
              approved portal or registration desk. The Wallet shall be linked to the
              patient's hospital number and personal or sponsor details.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Each User is responsible for maintaining the confidentiality of their login
              credentials and ensuring that no unauthorized person has access to the Wallet.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital reserves the right to suspend or deactivate any Wallet account in
              cases of suspected fraud, data inconsistency, or misuse.
            </p>
          </Section>

          <Section title="4. Funding of Wallet">
            <p className="text-sm text-gray-600 leading-relaxed">
              Wallets may be funded via bank transfer, POS, debit/credit card, or other
              hospital-approved payment channels. All payments must originate from a verified
              bank account bearing the name of the patient or their registered sponsor.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Any third-party funding not traceable to the patient's record may be subject to
              additional verification or delay.
            </p>
          </Section>

          <Section title="5. Use of Wallet Funds">
            <p className="text-sm text-gray-600 leading-relaxed">
              Funds in the Wallet may be used only for payments for hospital services,
              including consultation, treatment, admission, medication, diagnostics, and
              other approved purposes.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Wallet balances are non-transferable and cannot be used for cash withdrawals.
              The Wallet shall not accrue interest, dividends, or other financial returns.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital shall not be liable for unauthorized transactions resulting from
              the User's negligence, disclosure of credentials, or unauthorized access.
            </p>
          </Section>

          <Section title="6. Refund Policy">
            <p className="text-sm font-semibold text-gray-800">a. Refund of Unspent Balance</p>
            <List
              items={[
                "Refunds for unspent balances shall be made only upon written request by the account holder or authorized representative.",
                "Refunds can only be paid into the same bank account that originally funded the Wallet.",
                "Refund requests shall be subject to verification of transaction history, identity, and compliance with hospital accounting procedures.",
                "Processing of refunds may take up to 10–15 working days after receipt of complete documentation.",
                "The Hospital reserves the right to deduct applicable administrative or processing fees before refund disbursement.",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">b. Non-Refundable Transactions</p>
            <List
              items={[
                "Payments already utilized for services rendered are non-refundable.",
                "The Hospital shall not be responsible for refunds arising from user error, duplicate payments, or cancelled appointments, except as expressly approved by management.",
              ]}
            />
          </Section>

          <Section title="7. Refund in Case of Death">
            <p className="text-sm text-gray-600 leading-relaxed">
              In the event of the death of a patient or Wallet holder, refund of any unspent
              balance shall be made only to:
            </p>
            <List
              items={[
                "The documented Next of Kin as recorded in the deceased's medical record, upon presentation of valid means of identification and official proof of death (death certificate or hospital confirmation);",
                "A person duly authorized under a Letter of Administration for the deceased's estate, accompanied by a valid ID card and any additional documents reasonably required by the Hospital.",
              ]}
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital reserves the right to verify all submitted documents with relevant
              authorities before processing any refund. The Hospital shall not be liable for
              any dispute or claim arising between relatives, beneficiaries, or third parties
              regarding entitlement to the refund. Refunds under this clause shall be made
              strictly through official hospital channels and not in cash.
            </p>
          </Section>

          <Section title="8. Dispute Resolution on Transactions">
            <p className="text-sm text-gray-600 leading-relaxed">
              Any dispute concerning Wallet transactions or balances must be reported within
              7 working days from the date of occurrence. The Hospital will investigate the
              issue and provide feedback within a reasonable period. The Hospital's liability
              for verified transaction errors shall not exceed the amount in dispute.
            </p>
          </Section>

          <Section title="9. Data Privacy and Security">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital shall process and store all patient and transaction data in
              compliance with the Nigeria Data Protection Act 2023 and other applicable data
              protection regulations. Your personal and medical information shall be kept
              confidential and used solely for the purpose of service delivery, internal
              audits, and regulatory compliance.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              By using the Wallet, you consent to the Hospital's processing of your data in
              accordance with its{" "}
              <a href="/privacy-policy" className="text-[#0d3d2e] font-semibold hover:underline">
                Privacy Policy
              </a>
              . The Hospital shall not be responsible for breaches resulting from
              third-party systems, internet providers, or force majeure events.
            </p>
          </Section>

          <Section title="10. Suspension or Termination of Wallet">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital may suspend or close a Wallet account if fraudulent or suspicious
              activity is detected; required by law, regulation, or internal audit; or the
              account remains dormant beyond a stipulated period.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Upon termination, any unspent funds shall be refunded in accordance with the
              Refund Policy clauses above.
            </p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital shall not be liable for any loss or damage arising from network
              failure, downtime, or payment gateway issues; unauthorized access caused by the
              User's negligence; or indirect, incidental, or consequential damages arising
              from use or inability to use the Wallet.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital's total liability shall not exceed the unspent Wallet balance at
              the time of claim.
            </p>
          </Section>

          <Section title="12. Governing Law and Jurisdiction">
            <p className="text-sm text-gray-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of
              the Federal Republic of Nigeria. Any dispute arising hereunder shall first be
              resolved amicably; failing which, it shall be referred to arbitration in Lagos
              State, in accordance with the Arbitration and Conciliation Act, Cap A18, LFN
              2004.
            </p>
          </Section>

          <Section title="13. Compliance">
            <p className="text-sm text-gray-600 leading-relaxed">
              All Users agree to comply with all applicable laws, regulations, and hospital
              policies governing electronic payments, patient records, and financial
              transactions.
            </p>
          </Section>

          <Section title="14. Amendments">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital reserves the right to modify these Terms at any time. Any updated
              version will be published on the Hospital's official website or displayed at
              designated service points. Continued use of the Wallet after notice of
              amendment constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="15. Miscellaneous">
            <p className="text-sm text-gray-600 leading-relaxed">
              These Terms constitute the entire agreement between the User and the Hospital
              with respect to the Wallet service. If any provision of these Terms is found
              invalid or unenforceable, the remaining provisions shall remain in full force
              and effect. The Hospital's failure to enforce any provision shall not
              constitute a waiver of that right.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              By registering and using the Hospital Wallet, you acknowledge that you have
              read, understood, and agreed to these Terms and Conditions.
            </p>
          </Section>

          <Section title="Contact Us">
            <p className="text-sm text-gray-600 leading-relaxed">
              For questions about these Terms, please visit our{" "}
              <a href="/support" className="text-[#0d3d2e] font-semibold hover:underline">
                Support &amp; Contact Us
              </a>{" "}
              page.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
